import _ from 'lodash';
import mapboxGl from 'mapbox-gl';
import unitFormatConverter from 'Services/unitFormatConverter/unitFormatConverter';
import { isNil, isString, isFunction } from 'lodash';

const iconsMapping = {
    'Unknown': 'danger',
    'Accident': 'accident',
    'Fog': 'fog',
    'Dangerous Conditions': 'danger',
    'Rain': 'rain',
    'Ice': 'ice',
    'Jam': 'incident',
    'Lane Closed': 'laneclosed',
    'Road Closed': 'roadclosed',
    'Road Works': 'roadworks',
    'Wind': 'wind',
    'Flooding': 'flooding',
    'Detour': 'detour',
    'Cluster': ''
};

function validateFunction(paramName, paramValue) {
    if (paramValue && !isFunction(paramValue)) {
        throw new Error(`${paramName} must be a function.`);
    }
}

// eslint-disable
/**
 * The IncidentMarker class is used to display incident markers on the map.
 * Clustered incidents are shown as a number and all the other incidents are shown with their appropriate category icon.
 *
 * The structure of the marker is very simple:
 * <div class="icon-marker icon-circle-..." style="opacity: 0.9; ...">
 *      <div class="icon-marker-inner ..." style="height: 20px; width: 20px;">
 *      </div>
 * </div>
 *
 * There are 3 ways of customizing the marker:
 * 1) You can do some basic customization such as size and opacity in the options.
 * 2) Pass CSS classes to the outer and inner containers.
 * 3) Use the markerContentFactory to pass a function that renders a complete custom HTML element.
 * @class IncidentMarker
 * @private
 * @constructor
 * @param {Object} [options] Marker options
 * @param {Object} [renderData] Incident Details feature: This is data provided by the service to render the marker.
 * @param {Number|Array} [options.popupOffset = 15] Popup offset.
 * Single number, vertical distance from the point or Array.
 * Negative offsets indicate left and up.
 * @param {Boolean} [options.showDefaultPopup = true] Shows default popup.
 * @param {Function} [options.markerFactory] A factory method for creating marker
 * instances. This method may have two parameters:
 * * a feature representing incident (regular or clustered)
 * * a default factory method which may be invoked to build a default marker for a given incident.
 * * an optional instance of an old marker which can be updated
 *
 * This method needs to return a {{#crossLink "Maps.Marker"}}{{/crossLink}} instance or null. The latter will
 * cause that given incident will not be rendered on a map.
 * @param {String} [options.units = 'metric'] Measurements units. Supported values:
 * 'metric', 'imperial'
 * @param {Function} [options.popupContentFactory] A function to render a custom element for the marker's popup.
 * In the function you have access to all the features provided by the service `popupContentFactory(feature)`.
 * This function must return a HTML element or String.
 * @param {Function} [options.cluster.popupContentFactory] A function to render a custom element for the cluster
 * marker's popup. In the function you have access to all the features provided by the service
 * `popupContentFactory(feature)`. This function must return a HTML element or String.
 * @param {Boolean} [options.cluster.disableOnClickBehavior = false] The flag which
 * allows you to disable default on 'click' behavior for cluster markers
 *
 * @param {Array} [renderData.geometry.coordinates] The marker center point (lng lat).
 * @param {String} [renderData.properties.incidentCategory] The category of the incident.
 * @param {String} [renderData.properties.incidentSeverity] The severity of the incident.
 * @param {String} [renderData.properties.clusterSize] The number of elements in the cluster.
 */
// eslint-disable
class IncidentMarker {
    constructor(options = {}, renderData) {

        this.options = options;
        this.renderData = this._validateRenderData(renderData);
        this.isCluster = this.renderData.properties && !isNil(this.renderData.properties.clusterSize);
        validateFunction('markerFactory', this.options.markerFactory);

        this._marker = this._renderMarker();
        this.update = this.update.bind(this);
        this.getElement = this.getElement.bind(this);
        this.getPopup = this.getPopup.bind(this);
        this.remove = this.remove.bind(this);
        this.addTo = this.addTo.bind(this);
    }

    _validateRenderData = (renderData) => {
        if (!(_.has(renderData, 'geometry.coordinates') &&
            _.has(renderData, 'properties') &&
            !_.isEmpty(renderData.properties))) {
            throw new Error('Incident Marker: RenderData has properties missing to render the element. ' +
                'Required properties: geometry.coordinates; properties;');
        }
        return renderData;
    };

    _getOptions = (severity, incidentCategory) => {
        const acceptedOptions = ['opacity', 'size'];
        const incidentCategoryIcon = this.isCluster ? '' : iconsMapping[incidentCategory];
        const newOptions = Object.assign({}, {
            outerClass: `tt-icon-marker tt-icon-circle-${severity} ${this.options.outerClass || ''}`,
            innerClass: `tt-icon-marker-inner tt-icon-${incidentCategoryIcon} ${this.options.innerClass || ''}`
        });

        Object.keys(this.options).forEach((opt) => {
            if (acceptedOptions.includes(opt)) {
                newOptions[opt] = this.options[opt];
            }
        });

        return newOptions;
    };

    _defaultMarkerContentFactory = (options) => {
        const outerEl = document.createElement('div');
        outerEl.className = options.outerClass;
        if (options.opacity) {
            outerEl.style.opacity = options.opacity;
        }

        const innerEl = document.createElement('div');
        innerEl.className = options.innerClass;
        if (options.size) {
            innerEl.style.height = `${options.size}px`;
            innerEl.style.width = `${options.size}px`;
            innerEl.style.lineHeight = `${options.size}px`;
            innerEl.style.fontSize = `${options.size / 2}px`;
        }

        if (this.isCluster) {
            const clusterSize = this.renderData.properties.clusterSize;
            innerEl.innerText = clusterSize < 100 ? clusterSize : '99+';
        }

        outerEl.appendChild(innerEl);
        return outerEl;
    }

    _onClusterClick = () => {
        const { properties } = this.renderData;
        if (properties.clusterBounds) {
            this.map.fitBounds([
                properties.clusterBounds[0].reverse(), // lng lan
                properties.clusterBounds[1].reverse() // lng lan
            ], {
                padding: 50
            });
            return;
        }

        this.map.flyTo({
            center: this.renderData.geometry.coordinates,
            zoom: this.map.getZoom() + 1
        });
    }

    _bindEventListeners = () => {
        if (this.isCluster) {
            if (this.options.cluster && this.options.cluster.disableOnClickBehavior) {
                return;
            }
            this._marker.getElement().addEventListener('click', this._onClusterClick);
        }
    }

    _removeEventListeners = () => {
        if (this.isCluster) {
            if (this.options.cluster && this.options.cluster.disableOnClickBehavior) {
                return;
            }
            this._marker.getElement().removeEventListener('click', this._onClusterClick);
        }
    }

    _setPopup = (marker) => {
        const shouldNotShowPopupForCluster = this.isCluster && (!this.options.cluster ||
            !this.options.cluster.popupContentFactory);
        if (shouldNotShowPopupForCluster || this.options.showDefaultPopup === false) {
            return;
        }

        marker.setPopup(this._createPopup());
    }

    _validateSeverity = (properties) => properties && properties.incidentSeverity || 'unknown';

    _validateIncidentCategory = (properties) => properties && properties.incidentCategory || 'Unknown';

    _validateTime = (properties) => properties && unitFormatConverter.formatTime(properties.delaySeconds);

    _validateDistance = (properties) => properties.lengthMeters &&
        unitFormatConverter.formatDistance(properties.lengthMeters, this.options.units === 'imperial');

    _validateRoadNumber = (properties) => properties && properties.roadNumber;

    _setPopupContent = (popup, popupContent) => {
        if (isString(popupContent)) {
            popup.setHTML(popupContent);
        } else if (popupContent instanceof window.HTMLElement) {
            popup.setDOMContent(popupContent);
        } else {
            throw new Error('Unsupported popup content type. Only "html" or "text" content types are supported.');
        }
    };

    _createPopupContent = () => {
        if (!this.isCluster && this.options.popupContentFactory) {
            return this.options.popupContentFactory(this.renderData);
        } else if (this.isCluster && this.options.cluster && this.options.cluster.popupContentFactory) {
            return this.options.cluster.popupContentFactory(this.renderData);
        }

        const properties = this.renderData.properties;

        const severity = this._validateSeverity(properties);
        const incidentCategory = this._validateIncidentCategory(properties);
        const time = this._validateTime(properties);
        const distance = this._validateDistance(properties);
        const roadNumber = this._validateRoadNumber(properties);

        return (
            `<div class="tt-traffic-details">
                <div class="tt-traffic-icon">
                    <div class="tt-icon-circle-${severity}">
                        <div class="tt-icon-${iconsMapping[incidentCategory]}"></div>
                    </div>
                </div>
                ${roadNumber ? `<div class="tt-road-shield">${roadNumber}</div>` : ''}
                <div class="tt-traffic-description">
                    <div class="tt-incident-category">${incidentCategory}</div>
                    <div
                        class="tt-incident-delay-length"
                        ${!time && !distance ? 'style="display:none;"' : ''}
                    >
                            ${time ? `<div class="tt-incident-delay">${time}</div>` : ''}
                            ${distance ? `<div class="tt-incident-length">${distance}</div>` : ''}
                    </div>
                </div>
            </div>`);
    }

    _createPopup = () => {
        const popupContent = this._createPopupContent();
        const popup = new mapboxGl.Popup({ offset: this.options.popupOffset || 15 });
        this._setPopupContent(popup, popupContent);
        return popup;
    };

    _defaultFactoryMethod = (feature) => {
        const { properties } = feature;
        const severity = this._validateSeverity(properties);
        const incidentCategory = this._validateIncidentCategory(properties);
        //we should think about using our wrapped markers here.
        const marker =
            new mapboxGl.Marker(this._defaultMarkerContentFactory(this._getOptions(severity, incidentCategory)));
        this._setPopup(marker);
        return marker;
    };

    _renderMarker = () => {
        const feature = this.renderData;
        let marker;
        if (this.options.markerFactory) {
            marker = this.options.markerFactory(feature, this._defaultFactoryMethod);
        } else {
            marker = this._defaultFactoryMethod(feature);
        }

        if (marker) {
            marker.setLngLat(feature.geometry.coordinates);
        }
        return marker;
    };

    _updateCustomMarker = (renderData) => {
        const oldMarker = this._marker;
        this._marker = this.options.markerFactory(renderData, this._defaultFactoryMethod, this._marker);
        if (oldMarker !== this._marker && oldMarker) {
            oldMarker.remove();
        }
        if (this._marker) {
            this._marker.setLngLat(renderData.geometry.coordinates);
            if (oldMarker !== this._marker && this.map) {
                this._marker.addTo(this.map.__om);
            }
        }
    };

    update(renderData) {
        if (!this._marker) {
            return;
        }
        this._validateRenderData(renderData);

        if (_.isEqual(renderData, this.renderData)) {
            return;
        }

        this._marker.setLngLat(renderData.geometry.coordinates);
        this.renderData = renderData;

        if (this.options.markerFactory) {
            this._updateCustomMarker(renderData);
        } else if (this._marker.getPopup()) {
            this._setPopupContent(this._marker.getPopup(), this._createPopupContent());
        }
    }

    getElement() {
        return this._marker.getElement();
    }

    getPopup() {
        return this._marker.getPopup();
    }

    getMarker() {
        return this._marker;
    }

    remove() {
        if (this._marker) {
            this._removeEventListeners();
            this._marker.remove();
        }
        this.map = null;
    }

    addTo(map) {
        this.map = map;
        if (this._marker) {
            this._marker.addTo(map.__om);
            this._bindEventListeners();
        }
        return this;
    }
}

export default IncidentMarker;



// WEBPACK FOOTER //
// ./map/display/markers/IncidentMarker.js